import { Model } from "pinia-orm";
import Testimony from "./Testimony";

class HistoricalItem extends Model {
  static entity = "historicalItems";
  static primaryKey = "uuid";

  static typeKey = "historicalItemType";

  static types() {
    return {
      ICPE: ICPE,
      RESTRICTION: Restriction,
      PRODUCTION: Product,
      POLLUTION: Pollution,
      ACTIVITY: Activity,
      ACCIDENT: Accident,
      ICPE_FUSION: ICPEFusion,
      RESTRICTION_FUSION: RestrictionFusion,
      POLLUTION_FUSION: PollutionFusion,
      PRODUCT_FUSION: ProductFusion,
      ACTIVITY_FUSION: ActivityFusion,
      ACCIDENT_FUSION: AccidentFusion,
      BOMBINGS: Bombings,
      BOMBINGS_FUSION: BombingsFusion,
    };
  }

  static fields() {
    return {
      uuid: this.uid(""),
      testimonial: this.belongsTo(Testimony, "uuid"),
      analyticalStandard: this.attr(null),
      tracerCompound: this.attr([]),
      quantificationLimit: this.attr(null),
      justification: this.attr(null),
      isSourceTakenIntoAccount: this.attr(null),
      label: this.attr(null),
      tagsType: this.attr([]),
      localization: this.string(""),
      incertitudeDescription: this.string(""),
      incertitudeCategory: this.string(""),
    };
  }
}

export class ICPE extends HistoricalItem {
  static entity = "icpes";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      regime: this.attr(null),
      startDateIndication: this.attr(null),
      startDateYear: this.attr(null),
      activityDone: this.attr(null),
      endDateIndication: this.attr(null),
      endDateYear: this.attr(null),
      documentType: this.attr(null),
      canPollute: this.attr(null),
      companyName: this.attr(null),
      sectionNumber: this.attr(null),
      sectionTitle: this.attr(null),
      observations: this.attr(null),
      historicalItemType: this.string("ICPE"),
    };
  }
}

export class ICPEFusion extends ICPE {
  static entity = "icpeFusions";
  static primaryKey = "uuid";
  static baseEntity = "icpes";

  static fields() {
    return {
      ...super.fields(),
      icpes: this.attr([]),
      historicalItemType: this.string("ICPE_FUSION"),
    };
  }
}

export class Restriction extends HistoricalItem {
  static entity = "restrictions";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      type: this.attr(null),
      documentType: this.attr(null),
      documentPhoto: this.attr(null),
      description: this.attr(null),
      historicalItemType: this.string("RESTRICTION"),
    };
  }
}

export class RestrictionFusion extends Restriction {
  static entity = "restrictionFusions";
  static primaryKey = "uuid";
  static baseEntity = "restrictions";

  static fields() {
    return {
      ...super.fields(),
      restrictions: this.attr([]),
      historicalItemType: this.string("RESTRICTION_FUSION"),
    };
  }
}

export class Pollution extends HistoricalItem {
  static entity = "pollutions";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      impactedEnvironments: this.attr([]),
      estimatedDepth: this.attr(null),
      description: this.attr(null),
      historicalItemType: this.string("POLLUTION"),
    };
  }
}

export class PollutionFusion extends Pollution {
  static entity = "pollutionFusions";
  static primaryKey = "uuid";
  static baseEntity = "pollutions";

  static fields() {
    return {
      ...super.fields(),
      pollutions: this.attr([]),
      historicalItemType: this.string("POLLUTION_FUSION"),
    };
  }
}

export class Product extends HistoricalItem {
  static entity = "products";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      type: this.attr(),
      nature: this.attr(),
      storageConditions: this.attr(),
      useConditions: this.attr(),
      disposalConditions: this.attr(),
      historicalItemType: this.string("PRODUCTION"),
    };
  }
}

export class ProductFusion extends Product {
  static entity = "productFusions";
  static primaryKey = "uuid";
  static baseEntity = "products";

  static fields() {
    return {
      ...super.fields(),
      products: this.attr([]),
      historicalItemType: this.string("PRODUCT_FUSION"),
    };
  }
}

export class Activity extends HistoricalItem {
  static entity = "activities";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      startDateIndication: this.attr(null),
      startDateYear: this.attr(null),
      endDateIndication: this.attr(null),
      endDateYear: this.attr(null),
      description: this.string(""),
      canPollute: this.boolean(false),
      observations: this.string(""),
      risksOfSitePollution: this.boolean(false),
      historicalItemType: this.string("ACTIVITY"),
    };
  }
}

export class ActivityFusion extends Activity {
  static entity = "activityFusions";
  static primaryKey = "uuid";
  static baseEntity = "activities";

  static fields() {
    return {
      ...super.fields(),
      activities: this.attr([]),
      historicalItemType: this.string("ACTIVITY_FUSION"),
    };
  }
}

export class Accident extends HistoricalItem {
  static entity = "accidents";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      dateIndication: this.attr(null),
      dateYear: this.attr(null),
      description: this.attr(null),
      canPollute: this.boolean(false),
      impactedEnvironments: this.attr([]),
      riskDetails: this.string(""),
      measures: this.string(""),
      reason: this.string(""),
      historicalItemType: this.string("ACCIDENT"),
    };
  }
}

export class AccidentFusion extends Accident {
  static entity = "accidentFusions";
  static primaryKey = "uuid";
  static baseEntity = "accidents";

  static fields() {
    return {
      ...super.fields(),
      accidents: this.attr([]),
      historicalItemType: this.string("ACCIDENT_FUSION"),
    };
  }
}
export class Bombings extends HistoricalItem {
  static entity = "bombings";
  static primaryKey = "uuid";
  static baseEntity = "historicalItems";

  static fields() {
    return {
      ...super.fields(),
      dateIndication: this.attr(null),
      dateYear: this.attr(null),
      description: this.string(""),
      historicalItemType: this.string("BOMBINGS"),
    };
  }
}

export class BombingsFusion extends Bombings {
  static entity = "bombingsFusions";
  static primaryKey = "uuid";
  static baseEntity = "bombings";

  static fields() {
    return {
      ...super.fields(),
      bombings: this.attr([]),
      historicalItemType: this.string("BOMBINGS_FUSION"),
    };
  }
}
